import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Code splitting: Lazy load all routes for better performance
// Critical routes loaded immediately
import Login from './pages/Login';
import Signup from './pages/Signup';
import TestFeed from './pages/TestFeed'; // Feed is now the homepage
import AuthCallback from './pages/AuthCallback';

// Password Reset pages (critical for user recovery)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// Non-critical routes lazy loaded
const Welcome = lazy(() => import('./pages/Welcome'));
const AskQuestion = lazy(() => import('./pages/AskQuestion'));
const QuestionDetail = lazy(() => import('./pages/QuestionDetail'));
const TopicPage = lazy(() => import('./pages/TopicPage'));
const TopicsPage = lazy(() => import('./pages/TopicsPage'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const SavedQuestions = lazy(() => import('./pages/SavedQuestions'));
const ModerationQueue = lazy(() => import('./pages/ModerationQueue'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminQuestions = lazy(() => import('./pages/AdminQuestions'));
const AdminAnswers = lazy(() => import('./pages/AdminAnswers'));
const AdminTopics = lazy(() => import('./pages/AdminTopics'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminSEO = lazy(() => import('./pages/AdminSEO'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Achievements = lazy(() => import('./pages/Achievements'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));

// SEO Routes - Load immediately (not lazy) for better crawlability  
import Sitemap from './pages/Sitemap';
import RobotsTxt from './pages/RobotsTxt';

// Router wrapper to handle OAuth callback synchronously
function AppRouter() {
  // CRITICAL: Check for session_id in URL fragment BEFORE routing
  // This prevents race conditions by processing OAuth callback first
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/" element={<TestFeed />} /> {/* Feed is now homepage */}
        <Route path="/welcome" element={<Welcome />} /> {/* Marketing page moved here */}
        <Route path="/ask" element={<AskQuestion />} />
        <Route path="/questions/:slug" element={<QuestionDetail />} />
        <Route path="/topics" element={<TopicsPage />} />
        <Route path="/topics/:topicSlug" element={<TopicPage />} />
        <Route path="/profile/:username" element={<UserProfile />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/settings/notifications" element={<NotificationSettings />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/saved" element={<SavedQuestions />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/questions" element={<AdminQuestions />} />
        <Route path="/admin/answers" element={<AdminAnswers />} />
        <Route path="/admin/topics" element={<AdminTopics />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/seo" element={<AdminSEO />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/moderation" element={<ModerationQueue />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        
        {/* SEO Routes */}
        <Route path="/sitemap.xml" element={<Sitemap />} />
        <Route path="/robots.txt" element={<RobotsTxt />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
